import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import { deleteTagOuvidoria, getTagsOuvidoria } from '@spark/services';

const filtrosIniciais = {
  id: '',
  nomeTag: '',
  descricao: '',
  dataCriacaoInicial: '',
  dataCriacaoFinal: '',
};

const formatDateTime = value => {
  if (!value) return '';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const useTagsOuvidoriaListagem = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [registroParaExcluir, setRegistroParaExcluir] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(20);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtrosUI, setFiltrosUI] = useState(filtrosIniciais);
  const hasLoadedOnce = useRef(false);

  const fetchData = useCallback(async () => {
    const isInitialLoad = !hasLoadedOnce.current;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      const response = await getTagsOuvidoria({
        ...filtrosUI,
        pagina: paginaAtual,
        limitePorPagina: quantidadePorPagina,
      });

      const itens = Array.isArray(response?.itens) ? response.itens : [];
      const normalizados = itens.map(item => ({
        id: item.id,
        nomeTag: item.nomeTag || '',
        descricao: item.descricao || '',
        prioridade: Boolean(item.prioridade),
        prioridadeLabel: item.prioridade ? 'Sim' : 'Nao',
        dataCriacao: item.dataCriacao || '',
        dataCriacaoFormatada: formatDateTime(item.dataCriacao),
      }));

      setRegistros(normalizados);
      setTotalRegistros(Number(response?.paginacao?.totalItens || 0));
    } catch (error) {
      console.error('Erro ao buscar tags da ouvidoria:', error);
      setRegistros([]);
      setTotalRegistros(0);
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
      setFilterLoading(false);
    }
  }, [filtrosUI, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback(newFilters => {
    setPaginaAtual(1);
    setFiltrosUI({
      id: newFilters.id || '',
      nomeTag: newFilters.nomeTag || '',
      descricao: newFilters.descricao || '',
      dataCriacaoInicial: newFilters.dataCriacaoInicial || '',
      dataCriacaoFinal: newFilters.dataCriacaoFinal || '',
    });
  }, []);

  const handleLimparFiltros = useCallback(() => {
    setPaginaAtual(1);
    setFiltrosUI(filtrosIniciais);
  }, []);

  const handleExcluir = useCallback(id => {
    setRegistroParaExcluir(id);
    setShowDeleteModal(true);
  }, []);

  const handleCancelarExclusao = useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setShowDeleteModal(false);
    setRegistroParaExcluir(null);
  }, [deleteLoading]);

  const handleConfirmarExclusao = useCallback(async () => {
    if (!registroParaExcluir) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteTagOuvidoria(registroParaExcluir);
      notification.success('Tag da ouvidoria excluida com sucesso!');
      setShowDeleteModal(false);
      setRegistroParaExcluir(null);
      await fetchData();
    } catch (error) {
      console.error('Erro ao excluir tag da ouvidoria:', error);
    } finally {
      setDeleteLoading(false);
    }
  }, [fetchData, registroParaExcluir]);

  return {
    data: registros,
    loading,
    filterLoading,
    deleteLoading,
    showDeleteModal,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    filtrosUI,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handleCancelarExclusao,
    handleConfirmarExclusao,
    setPaginaAtual,
    setQuantidadePorPagina,
    refetch: fetchData,
  };
};
